import { supabase } from '../lib/supabase';

import { smsAlertService } from './smsAlertService';

import { escalationService } from './escalationService';
import { pushNotificationService } from './pushNotificationService';


export const workflowBuilderService = {
  /**
   * Get all workflows for a company
   */
  async getWorkflows(companyId = null) {
    try {
      let query = supabase
        ?.from('automated_workflows')
        ?.select(`
          *,
          created_by_user:user_profiles!automated_workflows_created_by_fkey(full_name, email)
        `)
        ?.order('created_at', { ascending: false });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(workflow => ({
        id: workflow?.id,
        companyId: workflow?.company_id,
        createdBy: workflow?.created_by,
        createdByUser: workflow?.created_by_user,
        workflowName: workflow?.workflow_name,
        description: workflow?.description,
        status: workflow?.status,
        triggerType: workflow?.trigger_type,
        triggerConditions: workflow?.trigger_conditions || {},
        workflowNodes: workflow?.workflow_nodes || [],
        isTemplate: workflow?.is_template,
        version: workflow?.version,
        executionCount: workflow?.execution_count || 0,
        lastExecutedAt: workflow?.last_executed_at,
        createdAt: workflow?.created_at,
        updatedAt: workflow?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching workflows:', error);
      throw error;
    }
  },

  /**
   * Create new workflow
   */
  async createWorkflow(workflowData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      const { data, error } = await supabase
        ?.from('automated_workflows')
        ?.insert({
          company_id: employeeData?.company_id,
          created_by: user?.id,
          workflow_name: workflowData?.workflowName,
          description: workflowData?.description,
          status: workflowData?.status || 'draft',
          trigger_type: workflowData?.triggerType,
          trigger_conditions: workflowData?.triggerConditions || {},
          workflow_nodes: workflowData?.workflowNodes || [],
          is_template: workflowData?.isTemplate || false,
          version: 1
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating workflow:', error);
      throw error;
    }
  },

  /**
   * Update workflow
   */
  async updateWorkflow(workflowId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('automated_workflows')
        ?.update({
          workflow_name: updates?.workflowName,
          description: updates?.description,
          status: updates?.status,
          trigger_type: updates?.triggerType,
          trigger_conditions: updates?.triggerConditions,
          workflow_nodes: updates?.workflowNodes,
          version: supabase?.raw('version + 1'),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', workflowId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  },

  /**
   * Delete workflow
   */
  async deleteWorkflow(workflowId) {
    try {
      const { error } = await supabase
        ?.from('automated_workflows')
        ?.delete()
        ?.eq('id', workflowId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  },

  /**
   * Activate/Deactivate workflow
   */
  async toggleWorkflowStatus(workflowId, status) {
    try {
      const { data, error } = await supabase
        ?.from('automated_workflows')
        ?.update({ status, updated_at: new Date()?.toISOString() })
        ?.eq('id', workflowId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling workflow status:', error);
      throw error;
    }
  },

  /**
   * Test workflow with mock data
   */
  async testWorkflow(workflowId, mockData) {
    try {
      const { data: workflow } = await supabase
        ?.from('automated_workflows')
        ?.select('*')
        ?.eq('id', workflowId)
        ?.single();

      if (!workflow) throw new Error('Workflow not found');

      // Simulate workflow execution
      const executionLog = [];
      const nodes = workflow?.workflow_nodes || [];

      for (const node of nodes) {
        const result = await this.executeNode(node, mockData, true);
        executionLog?.push({
          nodeId: node?.id,
          nodeType: node?.type,
          status: result?.success ? 'success' : 'failed',
          message: result?.message,
          timestamp: new Date()?.toISOString()
        });

        // Stop execution if node fails
        if (!result?.success) break;
      }

      return {
        success: true,
        executionLog,
        message: 'Workflow test completed'
      };
    } catch (error) {
      console.error('Error testing workflow:', error);
      throw error;
    }
  },

  /**
   * Execute a single workflow node
   */
  async executeNode(node, data, isTest = false) {
    try {
      switch (node?.type) {
        case 'trigger':
          return this.executeTriggerNode(node, data);
        case 'condition':
          return this.executeConditionNode(node, data);
        case 'sms_alert':
          return isTest 
            ? { success: true, message: 'SMS alert would be sent' }
            : this.executeSMSAlertNode(node, data);
        case 'task_assignment':
          return isTest
            ? { success: true, message: 'Task would be created' }
            : this.executeTaskAssignmentNode(node, data);
        case 'escalation':
          return isTest
            ? { success: true, message: 'Alert would be escalated' }
            : this.executeEscalationNode(node, data);
        case 'email_notification':
          return isTest
            ? { success: true, message: 'Email would be sent' }
            : this.executeEmailNotificationNode(node, data);
        case 'delay':
          return { success: true, message: `Delay: ${node?.config?.delayMinutes} minutes` };
        default:
          return { success: false, message: `Unknown node type: ${node?.type}` };
      }
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Execute trigger node
   */
  executeTriggerNode(node, data) {
    const { triggerType, conditions } = node?.config || {};
    
    // Check if trigger conditions are met
    if (triggerType === 'ai_compliance_prediction') {
      const confidenceThreshold = conditions?.confidenceThreshold || 0.7;
      if (data?.confidence >= confidenceThreshold) {
        return { success: true, message: 'Trigger conditions met' };
      }
    } else if (triggerType === 'pest_pressure_spike') {
      const severityThreshold = conditions?.severityLevel || 'high';
      const severityOrder = { low: 1, medium: 2, high: 3, critical: 4 };
      if (severityOrder?.[data?.severity] >= severityOrder?.[severityThreshold]) {
        return { success: true, message: 'Trigger conditions met' };
      }
    }

    return { success: false, message: 'Trigger conditions not met' };
  },

  /**
   * Execute condition node
   */
  executeConditionNode(node, data) {
    const { conditionType, operator, value } = node?.config || {};
    
    let dataValue;
    if (conditionType === 'confidence_score') {
      dataValue = data?.confidence;
    } else if (conditionType === 'severity_level') {
      const severityOrder = { low: 1, medium: 2, high: 3, critical: 4 };
      dataValue = severityOrder?.[data?.severity];
      value = severityOrder?.[value];
    } else if (conditionType === 'time_of_day') {
      const hour = new Date()?.getHours();
      dataValue = hour;
    }

    switch (operator) {
      case 'greater_than':
        return { success: dataValue > value, message: `Condition: ${dataValue} > ${value}` };
      case 'less_than':
        return { success: dataValue < value, message: `Condition: ${dataValue} < ${value}` };
      case 'equals':
        return { success: dataValue === value, message: `Condition: ${dataValue} === ${value}` };
      case 'greater_or_equal':
        return { success: dataValue >= value, message: `Condition: ${dataValue} >= ${value}` };
      default:
        return { success: false, message: 'Invalid operator' };
    }
  },

  /**
   * Execute SMS alert node
   */
  async executeSMSAlertNode(node, data) {
    try {
      const { recipients, message } = node?.config || {};
      
      // Send SMS
      await smsAlertService?.sendAlert({
        recipients,
        message: this.replaceVariables(message, data)
      });

      // Also send push notification for instant delivery
      if (recipients && recipients?.length > 0) {
        await pushNotificationService?.sendNotification({
          userIds: recipients,
          type: 'workflow_sms_alert',
          title: 'SMS Alert Sent',
          body: this.replaceVariables(message, data),
          priority: 'high',
          requiresConfirmation: true,
          data: {
            workflowId: data?.workflowId,
            nodeId: node?.id,
            alertType: 'sms'
          }
        });
      }
      
      return { success: true, message: 'SMS alert sent successfully' };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Execute task assignment node
   */
  async executeTaskAssignmentNode(node, data) {
    try {
      const { assignedTo, taskTitle, taskDescription, priority } = node?.config || {};
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      // Create task
      const { data: task, error } = await supabase
        ?.from('ipm_tasks')
        ?.insert({
          company_id: employeeData?.company_id,
          title: this.replaceVariables(taskTitle, data),
          description: this.replaceVariables(taskDescription, data),
          priority: priority || 'medium',
          assigned_to: assignedTo,
          created_by: user?.id,
          status: 'assigned'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Send push notification to assigned user
      await pushNotificationService?.sendTemplatedNotification(
        'Task Assignment',
        [assignedTo],
        {
          task_title: task?.title,
          due_date: task?.due_date || 'Not specified'
        }
      );
      
      return { success: true, message: 'Task created and assigned successfully', taskId: task?.id };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Execute escalation node
   */
  async executeEscalationNode(node, data) {
    try {
      const { escalateTo, reason } = node?.config || {};
      
      // Create escalation
      await escalationService?.escalateAlert({
        alertId: data?.alertId,
        alertType: data?.alertType,
        escalatedTo: escalateTo,
        reason: this.replaceVariables(reason, data)
      });

      // Send urgent push notification
      await pushNotificationService?.sendTemplatedNotification(
        'Escalation Alert',
        [escalateTo],
        {
          original_assignee: data?.originalAssignee || 'Unknown',
          alert_title: data?.alertTitle || 'Alert'
        }
      );
      
      return { success: true, message: 'Alert escalated successfully' };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Execute email notification node
   */
  async executeEmailNotificationNode(node, data) {
    try {
      const { recipientRole, subject, message } = node?.config || {};
      
      // Get recipients
      const recipients = await this.getRecipientsByRole(recipientRole);

      // Send via unified notification service
      for (const recipient of recipients) {
        await supabase?.from('notification_queue')?.insert({
          user_id: recipient?.userId,
          notification_type: 'email',
          subject: subject || 'Automated Workflow Notification',
          message: message || data?.message,
          priority: data?.severity || 'medium',
          status: 'pending',
          metadata: { workflowId: node?.workflowId }
        });
      }

      return { success: true, message: `Email queued for ${recipients?.length} recipients` };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Get recipients by role
   */
  async getRecipientsByRole(role) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return [];

      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData) return [];

      const { data: recipients } = await supabase
        ?.from('company_employees')
        ?.select('user_id, user_profiles(full_name, email)')
        ?.eq('company_id', employeeData?.company_id)
        ?.eq('role', role)
        ?.eq('status', 'active');

      return recipients?.map(r => ({
        userId: r?.user_id,
        fullName: r?.user_profiles?.full_name,
        email: r?.user_profiles?.email,
        companyId: employeeData?.company_id
      })) || [];
    } catch (error) {
      console.error('Error getting recipients:', error);
      return [];
    }
  },

  /**
   * Get workflow templates
   */
  async getWorkflowTemplates() {
    return [
      {
        id: 'template-1',
        name: 'Organic Certification Violation Response',
        description: 'Automated response for predicted organic certification violations',
        triggerType: 'ai_compliance_prediction',
        nodes: [
          {
            id: 'node-1',
            type: 'trigger',
            config: {
              triggerType: 'ai_compliance_prediction',
              conditions: { confidenceThreshold: 0.8, violationType: 'organic_certification' }
            }
          },
          {
            id: 'node-2',
            type: 'sms_alert',
            config: {
              recipientRole: 'supervisor',
              message: 'URGENT: Potential organic certification violation detected',
              includeDetails: true
            }
          },
          {
            id: 'node-3',
            type: 'task_assignment',
            config: {
              taskTitle: 'Investigate Organic Compliance Issue',
              assignToRole: 'compliance_officer',
              priority: 'critical',
              dueInHours: 2
            }
          },
          {
            id: 'node-4',
            type: 'escalation',
            config: {
              escalateToRole: 'manager',
              notifyViaSms: true,
              notifyViaEmail: true
            }
          }
        ]
      },
      {
        id: 'template-2',
        name: 'Critical Pest Outbreak Response',
        description: 'Immediate response protocol for critical pest pressure spikes',
        triggerType: 'pest_pressure_spike',
        nodes: [
          {
            id: 'node-1',
            type: 'trigger',
            config: {
              triggerType: 'pest_pressure_spike',
              conditions: { severityLevel: 'critical' }
            }
          },
          {
            id: 'node-2',
            type: 'sms_alert',
            config: {
              recipientRole: 'field_technician',
              message: 'CRITICAL: Pest outbreak detected - immediate action required',
              includeDetails: true
            }
          },
          {
            id: 'node-3',
            type: 'task_assignment',
            config: {
              taskTitle: 'Emergency Pest Treatment',
              assignToRole: 'field_technician',
              priority: 'critical',
              dueInHours: 1
            }
          }
        ]
      },
      {
        id: 'template-3',
        name: 'Pesticide Application Limit Alert',
        description: 'Alert when approaching pesticide application limits',
        triggerType: 'ai_compliance_prediction',
        nodes: [
          {
            id: 'node-1',
            type: 'trigger',
            config: {
              triggerType: 'ai_compliance_prediction',
              conditions: { confidenceThreshold: 0.75, violationType: 'pesticide_limit' }
            }
          },
          {
            id: 'node-2',
            type: 'email_notification',
            config: {
              recipientRole: 'supervisor',
              subject: 'Pesticide Application Limit Warning',
              message: 'You are approaching pesticide application limits for this period'
            }
          },
          {
            id: 'node-3',
            type: 'task_assignment',
            config: {
              taskTitle: 'Review Pesticide Usage Records',
              assignToRole: 'compliance_officer',
              priority: 'high',
              dueInHours: 24
            }
          }
        ]
      }
    ];
  },

  /**
   * Validate workflow for circular dependencies and logic errors
   */
  validateWorkflow(workflow) {
    const errors = [];
    const nodes = workflow?.workflowNodes || [];

    // Check for at least one trigger node
    const triggerNodes = nodes?.filter(n => n?.type === 'trigger');
    if (triggerNodes?.length === 0) {
      errors?.push('Workflow must have at least one trigger node');
    }

    // Check for circular dependencies
    const visited = new Set();
    const recursionStack = new Set();

    const hasCycle = (nodeId) => {
      visited?.add(nodeId);
      recursionStack?.add(nodeId);

      const node = nodes?.find(n => n?.id === nodeId);
      const nextNodes = node?.connections || [];

      for (const nextId of nextNodes) {
        if (!visited?.has(nextId)) {
          if (hasCycle(nextId)) return true;
        } else if (recursionStack?.has(nextId)) {
          return true;
        }
      }

      recursionStack?.delete(nodeId);
      return false;
    };

    for (const node of nodes) {
      if (!visited?.has(node?.id)) {
        if (hasCycle(node?.id)) {
          errors?.push('Workflow contains circular dependencies');
          break;
        }
      }
    }

    // Check for orphaned nodes (no connections)
    const connectedNodes = new Set();
    nodes?.forEach(node => {
      (node?.connections || [])?.forEach(conn => connectedNodes?.add(conn));
    });

    const orphanedNodes = nodes?.filter(n => 
      n?.type !== 'trigger' && !connectedNodes?.has(n?.id)
    );

    if (orphanedNodes?.length > 0) {
      errors?.push(`${orphanedNodes?.length} orphaned node(s) detected`);
    }

    return {
      isValid: errors?.length === 0,
      errors
    };
  },

  /**
   * Export workflow as JSON
   */
  async exportWorkflow(workflowId) {
    try {
      const { data: workflow } = await supabase
        ?.from('automated_workflows')
        ?.select('*')
        ?.eq('id', workflowId)
        ?.single();

      if (!workflow) throw new Error('Workflow not found');

      const exportData = {
        workflowName: workflow?.workflow_name,
        description: workflow?.description,
        triggerType: workflow?.trigger_type,
        triggerConditions: workflow?.trigger_conditions,
        workflowNodes: workflow?.workflow_nodes,
        version: workflow?.version,
        exportedAt: new Date()?.toISOString()
      };

      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workflow-${workflow?.workflow_name?.replace(/\s+/g, '-')?.toLowerCase()}.json`;
      link?.click();
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error exporting workflow:', error);
      throw error;
    }
  },

  /**
   * Get workflow execution history
   */
  async getWorkflowExecutionHistory(workflowId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('workflow_execution_logs')
        ?.select('*')
        ?.eq('workflow_id', workflowId)
        ?.order('executed_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        workflowId: log?.workflow_id,
        status: log?.status,
        executionLog: log?.execution_log || [],
        triggerData: log?.trigger_data || {},
        executedAt: log?.executed_at,
        completedAt: log?.completed_at,
        errorMessage: log?.error_message
      })) || [];
    } catch (error) {
      console.error('Error fetching execution history:', error);
      throw error;
    }
  }
};